import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, ArrowRight, HelpCircle } from 'lucide-react';
import { PUBLIC_PLANS, SALES_MAILTO } from '../lib/publicSite';

const PricingPage = () => {
  const plans = PUBLIC_PLANS.map((plan) => ({
    ...plan,
    id: plan.name.toLowerCase(),
    ctaLink: plan.name === 'Enterprise' ? SALES_MAILTO : '/signup',
    badge: plan.name === 'Free' ? 'ALWAYS FREE' : plan.name === 'Pro' ? 'MOST POPULAR' : null,
    popular: plan.name === 'Pro',
  }));

  const faqs = [
    {
      q: 'Can I change plans?',
      a: 'Email bc2801@hotmail.com to change a plan. Checkout charges Pro as one $29 payment for the company. There is no per-user price and no yearly price.'
    },
    {
      q: 'What does checkout charge?',
      a: 'Free is $0. Pro is $29 flat for the company. Enterprise is not a self-serve charge.'
    },
    {
      q: 'Is there a setup fee?',
      a: 'No setup fee is charged at checkout. Free is $0 with up to 3 users and 100 work orders total. Pro is $29 for the company.'
    },
    {
      q: 'What happens after my trial ends?',
      a: 'A trial lasts 14 days and includes up to 5 users and 50 work orders. No card is required. It does not switch to Free on its own. Email bc2801@hotmail.com to switch to Free.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
                <Zap className="w-6 h-6 text-black" />
              </div>
              <span className="text-xl font-bold">WorkStream Pro</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/#features" className="text-slate-300 hover:text-white px-4 py-2">
                Features
              </Link>
              <Link to="/login" className="text-slate-300 hover:text-white px-4 py-2">
                Sign In
              </Link>
              <Link 
                to="/signup" 
                className="bg-amber-500 hover:bg-amber-600 text-black px-6 py-2 rounded-sm font-semibold"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-slate-400 mb-4">
            These prices match checkout. Free is $0. Pro is $29 per month flat for the company. Enterprise is contact us at bc2801@hotmail.com.
          </p>
          <p className="text-sm text-slate-500 mb-12">
            There is no yearly price and no per-user price. A new company starts a 14-day trial at $0 (up to 5 users and 50 work orders, no card). Free is a different plan: 3 users and 100 work orders total.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-lg p-8 relative ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-amber-500/10 to-cyan-500/10 border-2 border-amber-500 scale-105'
                    : 'bg-slate-900 border border-slate-700'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold ${
                    plan.popular 
                      ? 'bg-amber-500 text-black' 
                      : 'bg-slate-700 text-slate-300'
                  }`}>
                    {plan.badge}
                  </div>
                )}

                {/* Plan Name */}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm mb-6">{plan.description}</p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold">{plan.price}</span>
                    {plan.period ? <span className="text-slate-400">/{plan.period.replace('per ', '')}</span> : null}
                  </div>
                </div>

                {plan.name === 'Enterprise' ? (
                  <a
                    href={plan.ctaLink}
                    className="block w-full text-center py-3 rounded-sm font-semibold mb-6 transition-all bg-slate-800 hover:bg-slate-700 text-white border border-slate-700"
                  >
                    Contact us
                    <ArrowRight className="w-4 h-4 inline ml-2" />
                  </a>
                ) : (
                <Link
                  to={plan.ctaLink}
                  className={`block w-full text-center py-3 rounded-sm font-semibold mb-6 transition-all ${
                    plan.highlighted
                      ? 'bg-amber-500 hover:bg-amber-600 text-black'
                      : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
                )}

                {/* Features */}
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className={feature.includes(':') ? 'font-semibold' : ''}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Enterprise Contact */}
          <div className="mt-12 text-center">
            <p className="text-slate-400 mb-4">
              Enterprise is custom. Email bc2801@hotmail.com.
            </p>
            <a
              href="mailto:bc2801@hotmail.com"
              className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-medium"
            >
              Contact us for Enterprise
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Compare all features
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium">Features</th>
                  <th className="text-center py-4 px-4 font-bold">Free</th>
                  <th className="text-center py-4 px-4 font-bold">Pro</th>
                  <th className="text-center py-4 px-4 font-bold">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {[
                  { feature: 'Users', free: '3', pro: 'Unlimited', enterprise: 'Unlimited' },
                  { feature: 'Work orders (total)', free: '100', pro: 'Unlimited', enterprise: 'Unlimited' },
                  { feature: 'Mobile App', free: '✓', pro: '✓', enterprise: '✓' },
                  { feature: 'Asset Management', free: '✓', pro: '✓', enterprise: '✓' },
                  { feature: 'Preventive Maintenance', free: '–', pro: '✓', enterprise: '✓' },
                  { feature: 'Advanced Reporting', free: '–', pro: '✓', enterprise: '✓' },
                  { feature: 'API Access', free: '–', pro: '✓', enterprise: '✓' },
                  { feature: 'White Label', free: '–', pro: '–', enterprise: '✓' },
                  { feature: 'SSO Integration', free: '–', pro: '–', enterprise: '✓' },
                  { feature: 'Dedicated Support', free: '–', pro: '–', enterprise: '✓' }
                ].map((row, idx) => (
                  <tr key={idx}>
                    <td className="py-4 px-4 text-slate-300">{row.feature}</td>
                    <td className="py-4 px-4 text-center text-slate-400">{row.free}</td>
                    <td className="py-4 px-4 text-center text-slate-400">{row.pro}</td>
                    <td className="py-4 px-4 text-center text-slate-400">{row.enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Pricing FAQ
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-slate-900 border border-slate-700 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-2">{faq.q}</h3>
                    <p className="text-slate-400 text-sm">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-black mb-4">
            Ready to get started?
          </h2>
          <p className="text-xl text-black/80 mb-8">
            Start a 14-day trial (up to 5 users and 50 work orders). No credit card required.
          </p>
          <Link
            to="/signup"
            className="inline-block bg-black hover:bg-slate-900 text-white px-10 py-4 rounded-sm font-bold text-lg"
          >
            Start Free Trial
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
